import { Injectable, inject } from '@angular/core';
import {TenantService} from './tenant.service';
import {Course} from '../models/course.model';
import {Lesson} from '../models/lesson.model';
import {environment} from '../../environments/environment';
import {ProtectedResourceAccess} from '../models/protected-resource-access.model';
import {SupportedLanguage} from '../models/supported-language.model';



@Injectable({
  providedIn: 'root'
})
export class UrlBuilderService {
  private tenant = inject(TenantService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {

  }


  getFlagUrl(language: SupportedLanguage) {
    if (environment.production) {
      return `https://${environment.platformCloudfrontDomain}/assets/images/min-img/ISO/${language}.png`;
    }
    else {
      return `/assets/images/min-img/ISO/${language}.png`;
    }
  }

  buildImageUrl(imagePath:string) {

    let path:string;

    if (!imagePath.startsWith("/")) {
      path = `/${imagePath}`;
    }
    else {
      path = imagePath;
    }

    if (environment.production || environment.test) {
      return `https://${environment.platformCloudfrontDomain}/assets${path}`;
    }
    else {
      return `/assets${path}`;
    }
  }

  buildCourseThumbnailUrl(course: Course) {
    // If no direct URL is available, fall back to the original path construction
    const courseId = (() => {
      if (course?.isThumbnailChanged && course?.cloneType === "read-only") {
        return course.id;
      }

      return course?.sourceCourseId ? course.sourceCourseId : course.id;
    })()

    if (course && course.thumbnail) {
      //for the moment images are not fetched via the CDN
      //if (environment.production) {
      //  return `https://cdn.onlinecoursehost.com/${this.tenant.id}/${courseId}/thumbnail/${course.thumbnail}`;
      //}
      return `https://firebasestorage.googleapis.com/v0/b/${environment.firebase.storageBucket}/o/${this.tenant.id}%2F${courseId}%2Fthumbnail%2F${course.thumbnail}?alt=media`;
    }
    else {
      return null;
    }
  }

  buildLessonThumbailUrl(course: Course, lesson:Lesson) {
    // check if the lesson is a copy of another lesson
    const lessonId = lesson?.sourceLessonId ? lesson.sourceLessonId : lesson.id;
    // if the lesson is a copy, use the source course identified
    const courseId = (() => {

      if(course?.cloneType === "read-only") {
        return course?.sourceCourseId
      }

      return lesson?.sourceLessonId ? lesson.sourceLessonCourseId : course.id;

    })();

    if (course && lesson && lesson.thumbnail) {
      //for the moment images are not fetched via the CDN
      // in production, use the CDN
      //if (environment.production) {
      //  return `https://cdn.onlinecoursehost.com/${this.tenant.id}/${courseId}/protectedResources/${lessonId}/${lesson.thumbnail}`;
      //}
      // in development, hit the firebase storage directly
      return `https://firebasestorage.googleapis.com/v0/b/${environment.firebase.storageBucket}/o/${this.tenant.id}%2F${courseId}%2FprotectedResources%2F${lessonId}%2F${lesson.thumbnail}?alt=media`;
    }
    else {
      return null;
    }
  }

  /**
   * Which `protectedResources` folder a lesson's files actually live in.
   *
   * A clone does not necessarily own its own folder — until its resources are replaced it keeps
   * reading the source course's. Anything that WRITES to or DELETES from the folder has to know
   * that, which is why this is public rather than inlined here: deleting a clone's file at the
   * resolved location would take the file out from under the original course.
   */
  resolveResourceLocation(course: Course, lesson: Lesson): { courseId: string; lessonId: string } {
    if (course?.cloneType === "read-only") {
      // Read-only clones always use source course
      return {
        courseId: course?.sourceCourseId,
        lessonId: lesson?.sourceLessonId || lesson.id
      };
    }

    if (course?.cloneType === "synchronized") {
      // For synchronized clones, check if resources have been changed
      if (lesson?.isResourceChanged) {
        // Resources have been changed, use current course and lesson
        return {
          courseId: course.id,
          lessonId: lesson.id
        };
      } else {
        // Resources haven't been changed, use resource source information.
        // resourceSourceCourseId/LessonId and sourceLessonCourseId/Id are paired fields —
        // only use the courseId half when the matching lessonId half is also present,
        // otherwise we'd mix a source courseId with the clone's own lessonId and produce
        // a storage path that doesn't exist.
        if (lesson?.resourceSourceLessonId) {
          return {
            courseId: lesson.resourceSourceCourseId || course.id,
            lessonId: lesson.resourceSourceLessonId
          };
        }
        if (lesson?.sourceLessonId) {
          return {
            courseId: lesson.sourceLessonCourseId || course.id,
            lessonId: lesson.sourceLessonId
          };
        }
        return { courseId: course.id, lessonId: lesson.id };
      }
    }

    // Regular course types - use original logic
    return {
      courseId: lesson?.sourceLessonId ? lesson.sourceLessonCourseId : course.id,
      lessonId: lesson?.sourceLessonId ? lesson.sourceLessonId : lesson.id
    };
  }

  /**
   * Url for one of the extra files in a download lesson's bundle.
   *
   * The first file goes through `buildLessonResourceUrl`, because its object name is held in the
   * protectedResources doc the frontend is not allowed to read. The extra files carry their own
   * object name on the lesson, so they need no access lookup — the access check that matters
   * already happened: `lessonData[1].status` gates whether this lesson renders at all.
   */
  buildLessonFileUrl(course: Course, lesson: Lesson, secretName: string) {

    if (!course || !lesson || !secretName) {
      return null;
    }

    const { courseId, lessonId } = this.resolveResourceLocation(course, lesson);

    if (environment.production) {
      return `https://cdn.onlinecoursehost.com/${this.tenant.id}/${courseId}/protectedResources/${lessonId}/${secretName}`;
    }

    return `https://firebasestorage.googleapis.com/v0/b/${environment.firebase.storageBucket}/o/${this.tenant.id}%2F${courseId}%2FprotectedResources%2F${lessonId}%2F${secretName}?alt=media`;
  }

  buildLessonResourceUrl(course: Course, lesson:Lesson, access: ProtectedResourceAccess) {
    const { courseId, lessonId } = this.resolveResourceLocation(course, lesson);

    if (course && lesson && lesson.originalFileName) {
      const resourceName = access.secretResourceNameHls ? access.secretResourceNameHls : access.secretResourceName;
      // in production, use the CDN
      if (environment.production) {
        const url = `https://cdn.onlinecoursehost.com/${this.tenant.id}/${courseId}/protectedResources/${lessonId}/${resourceName}`;
        //console.log(`Fetching video from CDN: ${url}`)
        return url;
      }
      // in development, hit the firebase storage directly
      //console.log(`Fetching video from Firebase Storage: ${resourceName}`)
      return `https://firebasestorage.googleapis.com/v0/b/${environment.firebase.storageBucket}/o/${this.tenant.id}%2F${courseId}%2FprotectedResources%2F${lessonId}%2F${resourceName}?alt=media`;
    } else {
      return null;
    }
  }

}
